import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Button, Divider, IconButton, Menu, MenuItem,
  List, ListItem, ListItemText, ListItemIcon, Alert,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import HomeIcon from '@mui/icons-material/Home';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import CampaignIcon from '@mui/icons-material/Campaign';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import RichContent from '../../../components/RichContent';
import CourseModules from './CourseModules';
import CourseAnnouncements from './CourseAnnouncements';
import CourseSyllabus from './CourseSyllabus';
import courseService from '../../../services/courseService';

const SECTION_TYPES = [
  { type: 'front_page', label: 'Front Page', icon: HomeIcon },
  { type: 'modules', label: 'Modules', icon: ViewModuleIcon },
  { type: 'announcements', label: 'Announcements', icon: CampaignIcon },
  { type: 'assignments', label: 'Assignments', icon: AssignmentIcon },
  { type: 'syllabus', label: 'Syllabus', icon: MenuBookIcon },
];

const SECTION_ICON_MAP = Object.fromEntries(
  SECTION_TYPES.map(s => [s.type, s.icon])
);
const SECTION_LABEL_MAP = Object.fromEntries(
  SECTION_TYPES.map(s => [s.type, s.label])
);

function HomeSection({
  type, course, courseId, modules, assignments, isInstructor, onModulesChange,
  AssignmentsView,
}) {
  switch (type) {
    case 'front_page': {
      const frontPage = course.front_page;
      if (!frontPage) {
        if (isInstructor) {
          return (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.paper', borderRadius: 2, border: '2px dashed', borderColor: 'divider' }}>
              <HomeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.primary" gutterBottom>
                No front page set
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Create a page and set it as the front page to display it here.
              </Typography>
              <Button
                component={Link}
                to={`/courses/${courseId}/pages`}
                variant="contained"
                sx={{ textTransform: 'none' }}
              >
                Go to Pages
              </Button>
            </Box>
          );
        }
        return null;
      }
      return (
        <Box>
          {isInstructor && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                component={Link}
                to={`/courses/${courseId}/pages/${frontPage.id}/edit`}
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                sx={{ textTransform: 'none' }}
                aria-label="Edit front page"
              >
                Edit Page
              </Button>
            </Box>
          )}
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 3 }}>
            <RichContent html={frontPage.body} />
          </Box>
        </Box>
      );
    }
    case 'modules':
      return (
        <CourseModules
          courseId={courseId}
          modules={modules}
          assignments={assignments}
          isInstructor={isInstructor}
          onModulesChange={onModulesChange}
        />
      );
    case 'announcements':
      return (
        <CourseAnnouncements courseId={courseId} isInstructor={isInstructor} />
      );
    case 'assignments':
      return (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>Assignments</Typography>
            <Button
              component={Link}
              to={`/courses/${courseId}/assignments`}
              variant="outlined"
              size="small"
              sx={{ textTransform: 'none' }}
            >
              View All
            </Button>
          </Box>
          {assignments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'background.paper', borderRadius: 2, border: '2px dashed', borderColor: 'divider' }}>
              <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.primary" gutterBottom>
                No assignments yet
              </Typography>
              <Typography color="text.secondary">
                {isInstructor
                  ? 'Create your first assignment to get started.'
                  : 'Your instructor hasn\'t posted any assignments yet.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {assignments.slice(0, 5).map((assignment) => {
                const dueDate = assignment.due_date ? new Date(assignment.due_date) : null;
                const isPastDue = dueDate && dueDate < new Date();
                return (
                  <Box
                    key={assignment.id}
                    component={Link}
                    to={`/courses/${courseId}/assignments/${assignment.id}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      textDecoration: 'none',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600} color="text.primary">
                        {assignment.title}
                      </Typography>
                      {dueDate && (
                        <Typography variant="body2" color={isPastDue ? 'error.main' : 'text.secondary'}>
                          Due: {dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Typography>
                      )}
                    </Box>
                    <Typography fontWeight={600} color="text.secondary">
                      {assignment.points_possible} pts
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      );
    case 'syllabus':
      return (
        <CourseSyllabus course={course} isInstructor={isInstructor} />
      );
    default:
      return null;
  }
}

function CourseHome({
  course, courseId, modules, assignments, isInstructor, onModulesChange,
  onCourseUpdate, AssignmentsView,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [layout, setLayout] = useState(course.home_layout || []);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Sync layout when course data changes
  useEffect(() => {
    setLayout(course.home_layout || []);
  }, [course.home_layout]);

  const handleSaveLayout = async (newLayout) => {
    setSaving(true);
    setSaveError('');
    try {
      const updated = await courseService.updateCourse(courseId, {
        home_layout: newLayout,
      });
      setLayout(newLayout);
      if (onCourseUpdate) {
        onCourseUpdate(updated);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.home_layout?.[0]
        || err.response?.data?.detail
        || 'Failed to save home page layout.';
      setSaveError(errorMsg);
      console.error('Failed to save layout:', err.response?.data || err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddSection = (type) => {
    setAddMenuAnchor(null);
    const newLayout = [...layout, { type }];
    setLayout(newLayout);
    handleSaveLayout(newLayout);
  };

  const handleRemoveSection = (index) => {
    const newLayout = layout.filter((_, i) => i !== index);
    setLayout(newLayout);
    handleSaveLayout(newLayout);
  };

  const handleMoveSection = (index, direction) => {
    const newLayout = [...layout];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newLayout.length) return;
    [newLayout[index], newLayout[targetIndex]] = [newLayout[targetIndex], newLayout[index]];
    setLayout(newLayout);
    handleSaveLayout(newLayout);
  };

  // Types already in the layout
  const usedTypes = new Set(layout.map(s => s.type));
  const availableTypes = SECTION_TYPES.filter(s => !usedTypes.has(s.type));

  return (
    <Box>
      {/* Settings toggle for instructors */}
      {isInstructor && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant={settingsOpen ? 'contained' : 'outlined'}
            size="small"
            startIcon={<SettingsIcon />}
            onClick={() => setSettingsOpen(!settingsOpen)}
            sx={{ textTransform: 'none' }}
            aria-label={settingsOpen ? 'Close home page settings' : 'Open home page settings'}
            aria-expanded={settingsOpen}
          >
            {settingsOpen ? 'Done Editing' : 'Edit Layout'}
          </Button>
        </Box>
      )}

      {/* Settings panel */}
      {settingsOpen && (
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            mb: 3,
          }}
          role="region"
          aria-label="Home page layout settings"
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
            Home Page Sections
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add, remove, and reorder sections displayed on the course home page.
          </Typography>

          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
          )}

          <List sx={{ mb: 1 }}>
            {layout.map((section, idx) => {
              const Icon = SECTION_ICON_MAP[section.type] || ViewModuleIcon;
              return (
                <ListItem
                  key={`${section.type}-${idx}`}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: 'background.default',
                  }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSection(idx, -1)}
                        disabled={idx === 0 || saving}
                        aria-label={`Move ${SECTION_LABEL_MAP[section.type]} up`}
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveSection(idx, 1)}
                        disabled={idx === layout.length - 1 || saving}
                        aria-label={`Move ${SECTION_LABEL_MAP[section.type]} down`}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSection(idx)}
                        disabled={saving}
                        aria-label={`Remove ${SECTION_LABEL_MAP[section.type]} section`}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                    <DragIndicatorIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={SECTION_LABEL_MAP[section.type] || section.type}
                  />
                </ListItem>
              );
            })}
          </List>

          {availableTypes.length > 0 && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                disabled={saving}
                sx={{ textTransform: 'none' }}
                aria-label="Add a section to home page"
                aria-haspopup="true"
              >
                Add Section
              </Button>
              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
              >
                {availableTypes.map(({ type, label, icon: Icon }) => (
                  <MenuItem key={type} onClick={() => handleAddSection(type)}>
                    <ListItemIcon>
                      <Icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      )}

      {/* Rendered sections */}
      {layout.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2, border: '2px dashed', borderColor: 'divider' }}>
          <HomeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.primary" gutterBottom>
            {isInstructor ? 'No sections configured' : 'Home page is empty'}
          </Typography>
          <Typography color="text.secondary">
            {isInstructor
              ? 'Click "Edit Layout" above to add sections to your course home page.'
              : 'Your instructor hasn\'t configured the home page yet.'}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {layout.map((section, idx) => (
            <Box key={`${section.type}-${idx}`}>
              {idx > 0 && <Divider sx={{ mb: 4 }} />}
              <HomeSection
                type={section.type}
                course={course}
                courseId={courseId}
                modules={modules}
                assignments={assignments}
                isInstructor={isInstructor}
                onModulesChange={onModulesChange}
                AssignmentsView={AssignmentsView}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default CourseHome;
